import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useHistory } from 'react-router-dom';
import Button from '@insurancemenu/imenu-components-lib/dist/components/Button';
import FormsSpinner from 'components/common/loaders/FormSpinner';
import PlanAttributeBlock from 'components/common/rfq/PlanAttributeBlock';
import { IMInputText, IMSelect } from '@insurancemenu/imenu-components-lib';
import AttributeCoverageTag from 'components/common/rfq/AttributeCoveredTag';
import { useRFQSaveProductPageStore } from 'store/pages/rfq/rfq-template/save-product/pageStore';
import useRfqTemplate from 'global/hooks/useRfqTemplate';
import CollapsibleSection from 'components/common/general/CollapsibleSection';
import { COLLAPSIBLE_PLAN_ATTRIBUTES_SECTION_TEST_ID } from 'components/common/rfq/constants';
import { AttributeInfo } from 'store/modules/uwProducts/uwProductsModule';
import { PlanAttributeType } from 'types/productServiceV2';
import { toJS } from 'mobx';
import {
  SaveTemplatesPageContentStyled,
  SectionStyled,
  SectionTitleStyled,
  SpinnerBgStyled,
  StatisticsListStyled,
  StatisticsItemStyled,
  StatisticsTitleStyled,
  StatisticsValueStyled,
  PlanAttributesListStyled,
  FooterStyled,
  IMFormStyled,
  AttributesSectionStyled,
} from './styles';
import {
  SAVE_PRODUCT_TEMPLATE_ATTRIBUTES_TEST_ID,
  SAVE_PRODUCT_TEMPLATE_BACK_BTN_TEST_ID,
  SAVE_PRODUCT_TEMPLATE_COMPLETE_BTN_TEST_ID,
  SAVE_PRODUCT_TEMPLATE_FORM_TEST_ID,
  SAVE_PRODUCT_TEMPLATE_SPINNER_TEST_ID,
  SAVE_PRODUCT_TEMPLATE_STATISTICS_TEST_ID,
} from '../constants';

interface StatisticsItemsProps {
  attributesInfo: AttributeInfo[];
  getAttributesQuantityByType: (planType: PlanAttributeType) => number;
}

interface PlanAttributesListProps {
  attributesInfo: AttributeInfo[];
}

const StatisticsItems = ({ attributesInfo, getAttributesQuantityByType }: StatisticsItemsProps): JSX.Element[] => {
  const STATISTICS_DATA: Record<string, number> = Object.freeze({
    Total: attributesInfo.length,
    Covered: getAttributesQuantityByType('FILLED_COVERED'),
    'Not Covered': getAttributesQuantityByType('FILLED_UNCOVERED'),
  });

  return Object.keys(STATISTICS_DATA).map((key) => {
    return (
      <StatisticsItemStyled key={key}>
        <StatisticsTitleStyled>{key}</StatisticsTitleStyled>
        <StatisticsValueStyled>{STATISTICS_DATA[key]}</StatisticsValueStyled>
      </StatisticsItemStyled>
    );
  });
};

const PlanAttributesListItems = ({ attributesInfo }: PlanAttributesListProps): JSX.Element[] => {
  return attributesInfo.map(({ name, formValues, engValues, isCovered, type, maxWaiverMessage }) => {
    return (
      <PlanAttributeBlock
        key={name}
        label={name}
        headerElement={<AttributeCoverageTag isAttributeCovered={isCovered} />}
        planAttribute={formValues}
        engValues={engValues}
        isAttributeOpenByDefault={false}
        planType={type}
        maxWaiverMessage={maxWaiverMessage}
      />
    );
  });
};

const RFQSaveProductTemplate = observer((): JSX.Element => {
  // Common data
  const history = useHistory();
  const { templateId, pageType } = useRfqTemplate();

  const store = useRFQSaveProductPageStore();

  // Mobx store
  const {
    isLoading,
    requiredAttributes,
    customAttributes,
    updateTemplateForm,
    availableProductLinesOptions,
    availableStatesOptions,
    isStepCompleted,
    isTemplateInProgress,
    completeStep: updateTemplateAndCompleteStep,
    getAttributesQuantityByType,
    getInitialData,
    resetInitialData,
  } = store;

  const convertedRequiredAttributes = requiredAttributes.map((attr) => toJS(attr)).slice();
  const convertedCustomAttributes = customAttributes.map((attr) => toJS(attr)).slice();

  useEffect(() => {
    void getInitialData(templateId);
    return resetInitialData;
  }, []);

  const returnBack = (): void => {
    history.push(`/product-template-checklist/${templateId}?type=${pageType}`);
  };
  const onCompleteStep = async (): Promise<void> => {
    await updateTemplateAndCompleteStep();
    returnBack();
  };

  return (
    <SaveTemplatesPageContentStyled>
      <SectionStyled>
        <SectionTitleStyled>Product Info</SectionTitleStyled>
        <IMFormStyled
          formController={updateTemplateForm}
          id="product-info-form"
          data-test-id={SAVE_PRODUCT_TEMPLATE_FORM_TEST_ID}
        >
          <IMInputText name="internalName" label="Internal Product Name" size="full" disabled={!isTemplateInProgress} />
          <IMInputText name="name" label="External Product Name" size="full" disabled={!isTemplateInProgress} />
          <IMInputText name="effectiveFrom" label="Effective from" size="m" disabled />
          <IMInputText name="effectiveTo" label="Effective to" size="m" disabled />
          {/* TODO_NEW temporary fix. Should be disabled */}
          <IMSelect name="state" label="State" size="m" options={availableStatesOptions} />
          <IMSelect
            name="productLineId"
            label="Product Line"
            size="m"
            options={availableProductLinesOptions}
            disabled
          />
        </IMFormStyled>
      </SectionStyled>
      <SectionStyled>
        {isLoading ? (
          <SpinnerBgStyled>
            <FormsSpinner display={isLoading} data-test-id={SAVE_PRODUCT_TEMPLATE_SPINNER_TEST_ID} />
          </SpinnerBgStyled>
        ) : (
          <CollapsibleSection
            title="Product Attributes"
            isOpenByDefault={false}
            pinTitle
            data-test-id={COLLAPSIBLE_PLAN_ATTRIBUTES_SECTION_TEST_ID}
          >
            <StatisticsListStyled data-test-id={SAVE_PRODUCT_TEMPLATE_STATISTICS_TEST_ID}>
              <StatisticsItems
                attributesInfo={convertedRequiredAttributes}
                getAttributesQuantityByType={getAttributesQuantityByType}
              />
            </StatisticsListStyled>
            <PlanAttributesListStyled data-test-id={SAVE_PRODUCT_TEMPLATE_ATTRIBUTES_TEST_ID}>
              <AttributesSectionStyled title="Required Attributes">
                <PlanAttributesListItems attributesInfo={convertedRequiredAttributes} />
              </AttributesSectionStyled>
              {convertedCustomAttributes.length > 0 && (
                <AttributesSectionStyled title="Custom Attributes">
                  <PlanAttributesListItems attributesInfo={convertedCustomAttributes} />
                </AttributesSectionStyled>
              )}
            </PlanAttributesListStyled>
          </CollapsibleSection>
        )}
      </SectionStyled>
      <FooterStyled>
        <Button
          type="outlined"
          size="large"
          width="24rem"
          onClick={returnBack}
          data-test-id={SAVE_PRODUCT_TEMPLATE_BACK_BTN_TEST_ID}
        >
          Back
        </Button>
        {isTemplateInProgress && (
          <Button
            size="large"
            width="24rem"
            color="primary"
            onClick={onCompleteStep}
            disabled={isLoading || !updateTemplateForm.isFormValid()}
            data-test-id={SAVE_PRODUCT_TEMPLATE_COMPLETE_BTN_TEST_ID}
          >
            {isStepCompleted ? 'Update' : 'Complete'}
          </Button>
        )}
      </FooterStyled>
    </SaveTemplatesPageContentStyled>
  );
});

export default RFQSaveProductTemplate;
